import ajax from "./ajax";

export const requestBatches = (query) => {
  return ajax(`/batches`, { query });
};

/** Historical Blank → Color → Size demand from batches in a date range. */
export const requestBatchedBlanksDemandReport = (query = {}) => {
  return ajax(`/batches/blanks-demand-report`, { query });
};

/** Batch items whose blanks arrived today (for cross-batch barcode printing). */
export const requestArrivedBlanksForPrint = (query = { day: "today" }) => {
  return ajax(`/batches/arrived-blanks-for-print`, { query });
};

/** Print queue: scope=today | pending (on-hand / in-stock unprinted). */
export const requestBarcodePrintQueue = (query = { scope: "today" }) => {
  return ajax(`/batches/barcode-print-queue`, { query });
};

/** Purchased on a wholesaler PO, not yet received. */
export const requestAwaitingReceiveQueue = () => {
  return ajax(`/batches/awaiting-receive-queue`);
};

/** Pre-printed barcodes awaiting garment match after arrival. */
export const requestMatchQueue = () => {
  return ajax(`/batches/match-queue`);
};

/**
 * Cross-batch barcode items for the Batches → Barcodes tab.
 * `filter` may be a single id ("waiting") or an array of ids to AND together.
 */
export const requestBarcodeItems = (query = {}) => {
  const { filter, ...rest } = query;
  const normalizedFilter = Array.isArray(filter) ? filter.join(",") : filter;
  return ajax(`/batches/barcode-items`, {
    query: {
      ...rest,
      ...(normalizedFilter ? { filter: normalizedFilter } : {}),
    },
  });
};

/** Hide leftover / cancelled barcodes from the Barcodes tab without unbatching. */
export const archiveBarcodeItems = (items = []) => {
  return ajax(`/batches/barcode-items/archive`, {
    method: "POST",
    data: { items },
  });
};

/** Warehouse-safe stamp after a barcode PDF download (not the full PUT /batches/:id). */
export const stampBatchBarcodesDownloaded = (batchId, data = {}) => {
  return ajax(`/batches/${batchId}/barcode-downloaded`, {
    method: "POST",
    data,
  });
};

/** Allocate a short unique code for a Barcodes-tab PDF (printed as `{batchId}-{code}`). */
export const createBarcodeTabDownload = (data = {}) => {
  return ajax(`/batches/barcode-tab-downloads`, {
    method: "POST",
    data,
  });
};

/** Recent Barcodes-tab downloads for this warehouse. */
export const requestBarcodeTabDownloads = () => {
  return ajax(`/batches/barcode-tab-downloads`);
};

/** Void a pre-printed barcode so it re-enters the print queue. */
export const voidBatchItemBarcode = (batchId, itemId) => {
  return ajax(`/batches/${batchId}/void-barcode/${itemId}`, {
    method: "POST",
  });
};

/** Manually mark batch items as blanks arrived from wholesaler. */
export const markBatchItemsBlanksArrived = (batchId, data) => {
  return ajax(`/batches/${batchId}/mark-blanks-arrived`, {
    method: "POST",
    data,
  });
};

/** Batch only the selected orders' lines waiting on Items (admins). */
export function sendOrdersToProduction(orderIds) {
  return ajax(`/batches/from-orders`, {
    method: "POST",
    data: { orderIds },
    timeout: 120000,
  });
}

export function createBatch(data) {
  return ajax(`/batches`, { method: "POST", data });
}

export const requestBatchDetails = (id) => {
  return ajax(`/batches/${id}`);
};

/** Look up a batch by its human number (BSP-261007-014). */
export const requestBatchByNumber = (number) => {
  return ajax(`/batches/by-number/${encodeURIComponent(number)}`);
};

export const requestBatchNumbering = () => {
  return ajax(`/batches/numbering`);
};

export const updateBatchNumbering = (data) => {
  return ajax(`/batches/numbering`, { method: "PUT", data });
};

export const checkBatchPrefix = (prefix) => {
  return ajax(`/batches/numbering/prefix-available`, { query: { prefix } });
};

export const requestBatchItems = (id, query = {}) => {
  return ajax(`/batches/${id}/items`, { query });
};

export const requestBatchBarcodes = (id) => {
  return ajax(`/batches/${id}/barcodes`);
};

export const requestBatchItemsCount = (id) => {
  return ajax(`/batches/${id}/count`);
};

export const requestBatchItemDetails = (id) => {
  return ajax(`/batch-items/${id}`);
};

export const readyToShip = (id, data = {}) => {
  return ajax(`/batch-items/${id}`, {
    method: "PUT",
    ...(data && Object.keys(data).length ? { data } : {}),
  });
};

/** After QC pass (skipBin), handheld sort assigns the existing shipping bin. */
export const assignQcBin = (id) => {
  return ajax(`/batch-items/${id}`, {
    method: "PUT",
    data: { assignBin: true },
  });
};

/** Sort bins: QC-passed print barcode → the ship bin Pull already assigned. */
export const sortBinScan = (data) => {
  return ajax(`/batch-items/sort-scan`, {
    method: "POST",
    data,
  });
};

/** Pick Mode: mark a batch item picked by the logged-in scanner user. */
export const pickBatchItem = (id, data = {}) => {
  return ajax(`/batch-items/${id}/pick`, { method: "POST", data });
};

/** Packed station: stamp who packed this item. Does not ship it. */
export const packBatchItem = (id, data = {}) => {
  return ajax(`/batch-items/${encodeURIComponent(id)}/pack`, {
    method: "POST",
    data,
  });
};

/** PULL.1 §8 — staging / press label scan. */
export const stageBatchItem = (id, data = {}) => {
  return ajax(`/batch-items/${id}/stage`, { method: "POST", data });
};

/** Handheld floor: any printed label → pull or putaway. */
export const scanFloorLabel = (data = {}) => {
  return ajax(`/batches/floor/scan`, { method: "POST", data });
};

/** PULL.2 — label-driven pull pass */
export const requestBatchPull = (batchId) => {
  return ajax(`/batches/${batchId}/pull`);
};

export const scanBatchPullLabel = (batchId, data = {}) => {
  return ajax(`/batches/${batchId}/pull/scan`, { method: "POST", data });
};

export const shortBatchPullGroup = (batchId, groupId, data = {}) => {
  return ajax(
    `/batches/${batchId}/pull/groups/${encodeURIComponent(groupId)}/short`,
    { method: "POST", data }
  );
};

export const skipBatchPullGroup = (batchId, groupId) => {
  return ajax(
    `/batches/${batchId}/pull/groups/${encodeURIComponent(groupId)}/skip`,
    { method: "POST" }
  );
};

export const updateBatchPullLine = (batchId, lineId, data = {}) => {
  return ajax(`/batches/${batchId}/pull/lines/${encodeURIComponent(lineId)}`, {
    method: "PUT",
    data,
  });
};

export const completeBatchPull = (batchId, data = {}) => {
  return ajax(`/batches/${batchId}/pull/complete`, { method: "POST", data });
};

export const setBatchRouteAsYouPull = (batchId, routeAsYouPull) => {
  return ajax(`/batches/${batchId}/pull`, {
    method: "PATCH",
    data: { routeAsYouPull },
  });
};

export const requestBatchDistribute = (batchId) => {
  return ajax(`/batches/${batchId}/distribute`);
};

export const sendBatchPrintCart = (batchId) => {
  return ajax(`/batches/${batchId}/distribute/print-cart`, { method: "POST" });
};

export const sendBatchShipNow = (batchId) => {
  return ajax(`/batches/${batchId}/distribute/ship-now`, { method: "POST" });
};

export const scanBatchDistributeLabel = (batchId, data = {}) => {
  return ajax(`/batches/${batchId}/distribute/scan`, { method: "POST", data });
};

export const completeBatchDistribute = (batchId) => {
  return ajax(`/batches/${batchId}/distribute/complete`, { method: "POST" });
};

/** Opens HTML pull sheet in a new window (print → PDF). Uses ajax for auth. */
export const openBatchPullSheet = async (batchId) => {
  const res = await ajax(`/batches/${batchId}/pull/sheet`, {
    responseType: "text",
    transformResponse: [(d) => d],
  });
  const html = typeof res === "string" ? res : res?.data || "";
  const w = window.open("", "_blank");
  if (!w) throw new Error("Popup blocked — allow popups to print the sheet");
  w.document.write(html);
  w.document.close();
  return true;
};

export const getOrderItemsCount = (batchId) => {
  return ajax(`/orders/items/counts?batchId=${batchId}`);
};

/**
 * Order → item-id lists for barcode "X of Y" sequence.
 * Merges `/orders/items/counts` across one or more batches so partial
 * print runs still number against the full order, not the PDF subset.
 */
export const fetchMergedOrderItemsCounts = async (batchIds = []) => {
  const unique = [
    ...new Set(
      (Array.isArray(batchIds) ? batchIds : [batchIds])
        .map((id) => (id == null ? "" : String(id).trim()))
        .filter(Boolean)
    ),
  ];
  if (!unique.length) return {};
  // One /orders/items/counts call hydrates a whole batch (~4s). Firing every
  // id at once saturates Node when the Barcodes tab also loads.
  const CONCURRENCY = 4;
  const merged = {};
  for (let i = 0; i < unique.length; i += CONCURRENCY) {
    const maps = await Promise.all(
      unique.slice(i, i + CONCURRENCY).map((id) =>
        getOrderItemsCount(id)
          .then((res) => res?.data?.orders || {})
          .catch(() => ({}))
      )
    );
    Object.assign(merged, ...maps);
  }
  return merged;
};

export const zipImages = (batchId, itemIds = [], placements = []) => {
  const query = {};
  if (Array.isArray(itemIds) && itemIds.length > 0) {
    query.itemIds = itemIds.join(",");
  }
  if (Array.isArray(placements) && placements.length > 0) {
    query.placements = placements.join(",");
  }
  return ajax(`/batches/${batchId}/zip`, {
    server: "python",
    ...(Object.keys(query).length ? { query } : {}),
  });
};

export const getBatchShipments = (batchId) => {
  return ajax(`/shipments/batches/${batchId}`);
};

export const fetchBatchXmlDetails = (batchId) => {
  return ajax(`/batches/${batchId}/xml-download`);
};

/** Popup "Got it" / "Continue anyway" on a cancelled item. */
export const acknowledgeCancelledItem = (batchItemId, { override = false } = {}) => {
  return ajax(`/batch-items/${encodeURIComponent(batchItemId)}/cancelled-ack`, {
    method: "POST",
    data: { override: override === true },
  });
};

export const unBatch = (batchItemId, data) => {
  return ajax(`/batch-items/${batchItemId}/unbatch`, { method: "PUT", data });
};

export const archiveBatch = (batchItemId, data) => {
  return ajax(`/batches/${batchItemId}/archive`, { method: "POST", data });
};

export const unarchiveBatch = (batchItemId, data) => {
  return ajax(`/batches/${batchItemId}/unarchive`, { method: "POST", data });
};

export const unBatchEntireBatch = (batchId) => {
  return ajax(`batches/${batchId}/unbatch`, { method: "PUT" });
};

export const unbatchSelectedItems = (batchId, data) => {
  return ajax(`/batches/${batchId}/unbatch-items`, { method: "PUT", data });
};

export const updateBatch = (batchItemId, data) => {
  return ajax(`/batches/${batchItemId}`, { method: "PUT", data });
};

export const getBarcodePrintRuns = (batchId) => {
  return ajax(`/batches/${batchId}/print-runs`);
};

export const refreshBatch = (batchId) => {
  return ajax(`/batches/${batchId}/refresh`, { method: "PUT" });
};

export const requestQualityControlFailedItems = () => {
  return ajax("/batch-items/qc-fails");
};

export const assignImageBin = (id, data = {}) => {
  return ajax(`/batches/${id}/bin-images`, { method: "POST", data });
};

export const sendToOrderdesk = (data) => {
  return ajax(`/v2/orderdesk/orders`, {
    method: "POST",
    data,
    server: "python",
  });
};

export const createBatchLabels = (id) => {
  return ajax(`v2/shipments/ship-batch/${id}`, {
    method: "POST",
    server: "python",
  });
};

export const requestAqcChecks = (batchItemId) => {
  return ajax(`/batch-items/${batchItemId}/aqc-checks`);
};

export const replaceBatchItemPrintFile = (batchItemId, data) => {
  return ajax(`/batch-items/${batchItemId}/replace-image`, {
    method: "POST",
    data,
  });
};

export const createAqcMismatchItem = (batchItemId, data) => {
  return ajax(`/batch-items/${batchItemId}/aqc-mismatch-item`, {
    method: "POST",
    data,
  });
};

export const requestAqcMismatchItems = () => {
  return ajax("/batch-items/aqc-mismatch-items");
};

export const postImageProcessingOptions = (data) => {
  // you can switch to another endpoint if needed, one is gcf and another is cloud run
  // gcf triggers the pub/sub which then trigger the cloud run service to process images
  return ajax(
    // "https://us-central1-riverr-enterprise-user.cloudfunctions.net/batchImageProcess2",
    "https://batch-images-process2-1032772938785.us-central1.run.app/batchImageProcess2",
    { method: "POST", data }
  );
};

export const updateBatchLabels = (batchId, data) => {
  return ajax(`/batches/${batchId}/labels`, {
    method: "PUT",
    data,
  });
};

export const proxyExternalImage = (url, { maxWidth } = {}) => {
  const query = { url };
  if (maxWidth != null && maxWidth !== "") {
    query.maxWidth = maxWidth;
  }
  return ajax(`/batches/proxy-external-image`, { query });
};

/** Create draft purchase order(s) from a batch's blank requirements (one PO per supplier). */
export const createPurchaseOrdersFromBatch = (batchId) => {
  return ajax(`/batches/${batchId}/purchase-orders`, { method: "POST", data: {} });
};

/** TIX.1 — Manufacturing Tickets rail (auto_pos batch projection). */
export const getTicketRail = (query = {}) => {
  return ajax(`/batches/ticket-rail`, { query });
};

export const claimTicket = (batchId, data = {}) => {
  return ajax(`/batches/${batchId}/ticket-claim`, { method: "POST", data });
};

export const advanceTicket = (batchId) => {
  return ajax(`/batches/${batchId}/ticket-advance`, { method: "POST", data: {} });
};

export const completeTicket = (batchId, data = {}) => {
  return ajax(`/batches/${batchId}/ticket-complete`, {
    method: "POST",
    data,
  });
};

export const recallTicket = (batchId) => {
  return ajax(`/batches/${batchId}/ticket-recall`, { method: "POST", data: {} });
};

export const setTicketStation = (batchId, station) => {
  return ajax(`/batches/${batchId}/ticket-station`, {
    method: "POST",
    data: { station },
  });
};

/**
 * ERP hold override. `id` may be an order id or a batch id. With
 * `{ allowPartial: true }` the order is released and batched with only the
 * lines that already have labels.
 */
export const printOverrideBatch = (id, data = { allowPartial: true }) => {
  return ajax(`/batches/${encodeURIComponent(id)}/print-override`, {
    method: "POST",
    data,
  });
};

/**
 * Reprint one item's label. `batchIdOrRef` is the batch id, number or work
 * id. `data` carries one of `{ itemId }`, `{ externalItemId }`, `{ gtin }`
 * plus `{ quantity }`. Returns `{ items: [{ itemId, printInfo }], reprintId }`.
 */
export const reprintBatchItem = (batchIdOrRef, data) => {
  return ajax(`/batches/${encodeURIComponent(batchIdOrRef)}/reprint`, {
    method: "POST",
    data,
  });
};
